import api from './api';
import { MediaUploadResponse, ApiResponse } from '../types';

interface MediaUploadResult {
  azureUrl: string;
  containerName: string;
  _id: string;
}

export const mediaService = {
  async uploadMedia(files: File[], containerName: string): Promise<MediaUploadResult[]> {
    if (files.length === 0) return [];

    const formData = new FormData();
    formData.append('containerName', containerName);

    if (files.length === 1) {
      // Single-file endpoint: multer.single('file')
      formData.append('file', files[0]);
      const response = await api.post('/media/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 0, // Disable timeout for large uploads
      });
      return [response.data.data];
    }

    // Multiple-files endpoint: multer.array('files', 10)
    files.forEach((file) => {
      formData.append('files', file);
    });
    const response = await api.post('/media/upload-multiple', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 0, // Disable timeout for large uploads
    });
    const responseData = response.data.data;
    return Array.isArray(responseData) ? responseData : [responseData];
  },

  async uploadSingleMedia(file: File, containerName: string): Promise<MediaUploadResult> {
    const results = await this.uploadMedia([file], containerName);
    return results[0];
  },

  // Upload profile image and return azureUrl
  async uploadProfileImage(file: File): Promise<string> {
    const result = await this.uploadSingleMedia(file, 'profiles');
    return result.azureUrl;
  },

  // Upload blog media and return azureUrl
  async uploadBlogMedia(files: File[]): Promise<string[]> {
    const results = await this.uploadMedia(files, 'blogs');
    return results.map(result => result.azureUrl);
  },

  // Upload single blog media
  async uploadSingleBlogMedia(file: File): Promise<string> {
    const result = await this.uploadSingleMedia(file, 'blogs');
    return result.azureUrl;
  },

  // Upload documents and return _id array
  async uploadDocuments(files: File[]): Promise<string[]> {
    const results = await this.uploadMedia(files, 'partner-docs');
    return results.map(result => result._id);
  },

  async deleteMedia(mediaId: string): Promise<ApiResponse<void>> {
    const response = await api.delete(`/media/${mediaId}`);
    return response.data;
  },

  async getMediaById(mediaId: string): Promise<ApiResponse<MediaUploadResponse>> {
    const response = await api.get(`/media/${mediaId}`);
    return response.data;
  }
};
